import React, { useContext, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import LoggedInLayout from "../layouts/LoggedInLayout";
import DefaultLayout from "../layouts/DefaultLayout";
import BackdropLoader from "../components/common/BackdropLoader";
import { AccountContext } from "../store/AccountContext";
import { storeReturnPath } from "../utils/returnPath";
import { redeemLoginCode } from "../api/client";
import { NoConnectionError, ValidationError } from "../api/errors";

// The TV shows a 16-character code grouped as XXXX-XXXX-XXXX-XXXX. The
// backend stores and matches the code WITH hyphens, and its charset has
// no zero (only O), so zeros are corrected rather than rejected.
const CODE_LENGTH = 19;

const normalise = (raw: string): string =>
  raw
    .toUpperCase()
    .replace(/0/g, "O")
    .replace(/[^A-Z1-9]/g, "");

export const formatLoginCode = (raw: string): string =>
  normalise(raw)
    .slice(0, 16)
    .replace(/(.{4})(?=.)/g, "$1-");

// Caret index in the formatted string after n code characters (a hyphen
// follows every complete group of four except at the very end).
export const caretIndexAfter = (n: number): number => {
  const capped = Math.min(Math.max(n, 0), 16);
  return capped + Math.floor(Math.max(capped - 1, 0) / 4);
};

type RedemptionError = "invalid" | "connection" | "server";

const ERROR_COPY: { [key in RedemptionError]: string } = {
  invalid:
    "That code doesn't match a TV. Check it against the code on your TV screen and try again.",
  connection:
    "We couldn't reach FarmFLiX. Check your internet connection and try again.",
  server: "Something went wrong at our end. Please try again in a minute.",
};

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    padding: 0,
    margin: 0,
    "@media (min-width:600px)": {
      margin: "0 auto",
    },
  },
  paper: {
    borderRadius: "0px",
    display: "flex",
    flexDirection: "column",
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    "@media (min-width:600px)": {
      // @ts-ignore
      backgroundColor: theme.palette.background.ffNavyLighter,
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
    },
  },
  formHeader: {
    color: theme.palette.common.white,
    fontSize: "2.2rem",
    textAlign: "center",
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    "@media (min-width:600px)": {
      fontSize: "2.2rem",
      lineHeight: "2.4rem",
    },
    "@media (min-width:960px)": {
      fontSize: "2.6rem",
      lineHeight: "2.8rem",
    },
  },
  successHeader: {
    // @ts-ignore
    color: theme.palette.background.ffGreen,
    fontSize: "2.2rem",
    textAlign: "center",
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    outline: "none",
    "@media (min-width:600px)": {
      fontSize: "2.2rem",
      lineHeight: "2.4rem",
    },
    "@media (min-width:960px)": {
      fontSize: "2.6rem",
      lineHeight: "2.8rem",
    },
  },
  bodyText: {
    color: theme.palette.common.white,
    marginBottom: theme.spacing(2),
    textAlign: "center",
  },
  hintText: {
    color: theme.palette.common.darkgray,
    textAlign: "center",
    marginTop: theme.spacing(2),
  },
  codeDisplay: {
    color: theme.palette.common.white,
    textAlign: "center",
    fontSize: "1.8rem",
    fontWeight: 600,
    letterSpacing: "3px",
    fontFamily: "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  background: {
    width: "100%",
    height: "100%",
    flexGrow: 1,
    // @ts-ignore
    backgroundColor: theme.palette.background.dark,
    paddingBottom: theme.spacing(4),
  },
  validationError: {
    marginBottom: theme.spacing(2),
  },
  codeField: {
    "& input": {
      backgroundColor: "#444",
      color: "#fff",
      borderColor: "#444",
      textAlign: "center",
      fontSize: "1.1rem",
      fontWeight: 600,
      letterSpacing: "1px",
      textTransform: "uppercase",
    },
    "@media (min-width:400px)": {
      "& input": {
        fontSize: "1.4rem",
        letterSpacing: "2px",
      },
    },
  },
  submit: {
    margin: theme.spacing(1.5, 0, 2),
  },
  secButton: {
    margin: theme.spacing(1.5, 0, 2),
    borderColor: "#e9e9e9",
    color: "#e9e9e9",
    "&:hover": {
      borderColor: "#fff",
      color: "#fff",
    },
  },
  videosLink: {
    textDecoration: "underline",
    textDecorationColor: "#99ca3c",
  },
  videosLinkText: {
    // @ts-ignore
    color: theme.palette.background.ffGreen,
    fontWeight: 400,
    textAlign: "center",
  },
  backdrop: {
    zIndex: 100000000,
    color: "#fff",
  },
}));

const TvActivation = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, loggedIn } = useContext(AccountContext);

  // Pre-fill from the ?code= the TV embeds in its QR-code URL. formatLoginCode
  // strips anything outside the code alphabet, so a malformed or absent param
  // simply normalises to "" and degrades to the empty form — never an error
  // screen. There is deliberately NO auto-submit (#544 AC2): redemption binds
  // the member's account to whichever TV generated the code, so the member
  // reviews the pre-filled code and taps the button — a stale bookmarked
  // /tv?code= link must never silently rebind their account to an old TV.
  const [code, setCode] = useState<string>(() =>
    formatLoginCode(new URLSearchParams(location.search).get("code") || ""),
  );
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState<RedemptionError | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const caretRef = useRef<number | null>(null);
  const successHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Reformatting resets the browser caret to the end; put it back where
  // the member was editing.
  useEffect(() => {
    if (caretRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(caretRef.current, caretRef.current);
      caretRef.current = null;
    }
  }, [code]);

  // The success panel replaces the focused submit button; move focus to
  // the confirmation heading so the outcome is announced.
  useEffect(() => {
    if (succeeded && successHeadingRef.current) {
      successHeadingRef.current.focus();
    }
  }, [succeeded]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart } = event.target;
    const caretCodeChars = normalise(
      value.slice(0, selectionStart === null ? value.length : selectionStart),
    ).length;

    let nextRaw = normalise(value);
    let caretAfter = caretCodeChars;

    // Deleting a hyphen removes no code characters, so the value would
    // not change and the field would drift out of sync — treat it as
    // deleting the code character before the caret instead.
    if (nextRaw === normalise(code) && value.length < code.length) {
      nextRaw =
        nextRaw.slice(0, Math.max(caretCodeChars - 1, 0)) +
        nextRaw.slice(caretCodeChars);
      caretAfter = Math.max(caretCodeChars - 1, 0);
    }

    const formatted = formatLoginCode(nextRaw);
    const caret = caretIndexAfter(caretAfter);
    setError(null);

    if (formatted === code) {
      // React won't re-render for an identical value; put the DOM back
      // in sync ourselves (a junk character typed mid-code would
      // otherwise linger on screen).
      event.target.value = formatted;
      event.target.setSelectionRange(caret, caret);
      return;
    }

    caretRef.current = caret;
    setCode(formatted);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (code.length !== CODE_LENGTH || submitting) {
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await redeemLoginCode(code);
      if (mountedRef.current) {
        setSucceeded(true);
      }
    } catch (e) {
      if (mountedRef.current) {
        if (e instanceof ValidationError) {
          setError("invalid");
        } else if (e instanceof NoConnectionError) {
          setError("connection");
        } else {
          setError("server");
        }
      }
    } finally {
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }
  };

  const reset = () => {
    setCode("");
    setError(null);
    setSucceeded(false);
  };

  // A logged-out visitor arrived here by scanning the TV's QR code. Remember
  // /tv?code=… so both sign-in forms return them straight back here after
  // login (#571), then hand off to the normal login flow — exactly what
  // LoggedInLayout would have done on their behalf, but behind an explicit,
  // explained action instead of a silent redirect.
  const goToSignIn = () => {
    storeReturnPath(location.pathname + location.search);
    navigate("/login");
  };

  const helmet = (
    <Helmet>
      <meta charSet="utf-8" />
      <title>FarmFLiX.tv | Log In Your TV</title>
      <meta
        name="description"
        content="Enter the code shown on your TV to log it in to FarmFLiX."
      />
    </Helmet>
  );

  // Session still being validated (an auth token is being checked). Show the
  // same loading shell LoggedInLayout uses so a member with a live session
  // never flashes the signed-out panel (mirrors LoggedInLayout.js:48-55).
  if (loading && !loggedIn) {
    return (
      <div>
        {helmet}
        <DefaultLayout showTry={false} showSignIn={false}>
          <Box className={classes.background} />
          <BackdropLoader />
        </DefaultLayout>
      </div>
    );
  }

  // Validated and signed out. LoggedInLayout would have bounced straight to
  // /login; instead we explain WHY (they scanned a TV) and let them choose,
  // keeping the pre-filled code visible so it survives the sign-in round-trip.
  if (!loggedIn) {
    return (
      <div>
        {helmet}
        <DefaultLayout>
          <Container maxWidth="xs" className={classes.container}>
            <Grid container className={classes.paper} spacing={2}>
              <Grid item xs={12}>
                <Typography
                  component="h1"
                  variant="h1"
                  className={classes.formHeader}
                >
                  {"Log In Your TV"}
                </Typography>
                <Typography
                  component="p"
                  variant="body2"
                  className={classes.bodyText}
                >
                  {
                    "Sign in to your FarmFLiX account and we'll log in the TV you scanned."
                  }
                </Typography>
                {code && (
                  <Typography
                    component="p"
                    variant="body1"
                    className={classes.codeDisplay}
                    aria-label={"Your TV code is " + code}
                  >
                    {code}
                  </Typography>
                )}
                <Button
                  name="signInToActivate"
                  fullWidth
                  size="medium"
                  className={classes.submit}
                  onClick={goToSignIn}
                >
                  {"Sign in to log in your TV"}
                </Button>
                <Typography
                  component="p"
                  variant="body2"
                  className={classes.hintText}
                >
                  {
                    "The code is on your TV's login screen, underneath the QR code."
                  }
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </DefaultLayout>
      </div>
    );
  }

  // Signed in: keep the existing LoggedInLayout wrapper untouched so the
  // membership-status redirects (expired / locked members) still apply.
  return (
    <div>
      {helmet}
      <LoggedInLayout>
        <Container maxWidth="xs" className={classes.container}>
          <Grid container className={classes.paper} spacing={2}>
            {succeeded ? (
              <Grid item xs={12}>
                <Typography
                  component="h1"
                  variant="h1"
                  className={classes.successHeader}
                  tabIndex={-1}
                  ref={successHeadingRef}
                >
                  {"Code accepted!"}
                </Typography>
                <Typography
                  component="p"
                  variant="body2"
                  className={classes.bodyText}
                >
                  {"Your TV should log in within a few seconds."}
                </Typography>
                <Typography
                  component="p"
                  variant="body2"
                  className={classes.hintText}
                >
                  {
                    "Nothing happening? Make sure your TV is on its login screen and the code on it matches the one you entered, then try again."
                  }
                </Typography>
                <Button
                  name="activateAnother"
                  fullWidth
                  size="medium"
                  variant="outlined"
                  className={classes.secButton}
                  onClick={reset}
                >
                  {"Log In Another TV"}
                </Button>
                <Link to="/videos" className={classes.videosLink}>
                  <Typography
                    variant="body2"
                    className={classes.videosLinkText}
                  >
                    {"Back to Videos"}
                  </Typography>
                </Link>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Typography
                  component="h1"
                  variant="h1"
                  className={classes.formHeader}
                >
                  {"Log In Your TV"}
                </Typography>
                <Typography
                  component="p"
                  variant="body2"
                  className={classes.bodyText}
                >
                  {
                    "Enter the code shown on your TV screen and we'll log it in for you."
                  }
                </Typography>
                {error && (
                  <Alert
                    className={classes.validationError}
                    severity="error"
                    variant="filled"
                  >
                    {ERROR_COPY[error]}
                  </Alert>
                )}
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    id="tv-code"
                    label="TV Code"
                    value={code}
                    onChange={handleChange}
                    disabled={submitting}
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    className={classes.codeField}
                    inputRef={inputRef}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      autoCapitalize: "characters",
                      autoCorrect: "off",
                      spellCheck: false,
                    }}
                  />
                  <Button
                    name="activateTv"
                    fullWidth
                    size="medium"
                    type="submit"
                    className={classes.submit}
                    disabled={code.length !== CODE_LENGTH || submitting}
                  >
                    {"Log In TV"}
                  </Button>
                  {submitting && (
                    <Backdrop className={classes.backdrop} open={true}>
                      <CircularProgress color="primary" />
                    </Backdrop>
                  )}
                </form>
                <Typography
                  component="p"
                  variant="body2"
                  className={classes.hintText}
                >
                  {
                    "The code is on your TV's login screen, underneath the QR code."
                  }
                </Typography>
              </Grid>
            )}
          </Grid>
        </Container>
      </LoggedInLayout>
    </div>
  );
};

export default TvActivation;
